/**
 * Property & rentals — the vocabulary shared by the app and Convex.
 *
 * A landlord's world has three records the hotel never needed: the
 * PROPERTY (a building, an arcade, a plot), its UNITS (Shop 14, flat 3B —
 * each its own rent, tenant and meter), and the TENANCY (who holds the unit,
 * from when, at what rent). Money moves in PERIODS: one bill per interval,
 * every period an ordinary `orders` row (paymentTerms "rent") so receipts,
 * WhatsApp and Cash Flow work untouched.
 *
 * A LISTING is a different thing again — a product with a `property` bucket
 * that the storefront shows for rent or for sale. It may point at a managed
 * unit or property, or stand alone (an agent's client's house).
 *
 * Days are YYYYMMDD ints in EAT, the accommodation vocabulary
 * (lib/config/accommodation.ts). Plain TypeScript: land sizes come from
 * lib/config/landArea.ts and other currencies from lib/config/currencies.ts.
 */

import type { BusinessKind } from "./businessKinds";
import { addDays, formatDayInt, isValidDayInt } from "./accommodation";
import { BASE_CURRENCY, cleanForeignPrice, cleanRate, formatIn, fromBase, isForeignCode, roundIn, toBase, websitePrice, type Fx } from "./currencies";
import { formatLand, isLandUnit, type LandUnit } from "./landArea";

export const PROPERTY_INDUSTRY_SLUG = "property" as const;
export const PROPERTY_KIND: BusinessKind = "property";

// ── Pick lists ─────────────────────────────────────────────────────────────

export type PropertyKind = "residential" | "commercial" | "mixed" | "land";
export const PROPERTY_KINDS: ReadonlyArray<{ value: PropertyKind; label: string }> = [
  { value: "residential", label: "Residential" },
  { value: "commercial", label: "Commercial" },
  { value: "mixed", label: "Mixed use" },
  { value: "land", label: "Land" },
];

export const UNIT_KINDS: ReadonlyArray<{ value: string; label: string; group: "home" | "business" }> = [
  { value: "single_room", label: "Single room", group: "home" },
  { value: "double_room", label: "Double room", group: "home" },
  { value: "self_contained", label: "Self-contained", group: "home" },
  { value: "bed1", label: "1 bedroom", group: "home" },
  { value: "bed2", label: "2 bedroom", group: "home" },
  { value: "bed3", label: "3 bedroom", group: "home" },
  { value: "house", label: "House", group: "home" },
  { value: "shop", label: "Shop", group: "business" },
  { value: "stall", label: "Stall", group: "business" },
  { value: "office", label: "Office", group: "business" },
  { value: "warehouse", label: "Warehouse / store", group: "business" },
  { value: "plot", label: "Plot", group: "business" },
];

export type RentInterval = "monthly" | "quarterly" | "semiannual" | "annual";
export const RENT_INTERVALS: ReadonlyArray<{ value: RentInterval; label: string; months: number; per: string }> = [
  { value: "monthly", label: "Monthly", months: 1, per: "month" },
  { value: "quarterly", label: "Every 3 months", months: 3, per: "quarter" },
  { value: "semiannual", label: "Every 6 months", months: 6, per: "half-year" },
  { value: "annual", label: "Yearly", months: 12, per: "year" },
];

export function intervalMonths(interval: string | undefined): number {
  return RENT_INTERVALS.find((i) => i.value === interval)?.months ?? 1;
}

export function intervalLabel(interval: string | undefined): string {
  return RENT_INTERVALS.find((i) => i.value === interval)?.label ?? "Monthly";
}

/** "per month", "per quarter" — for the rent figure. */
export function intervalPer(interval: string | undefined): string {
  return RENT_INTERVALS.find((i) => i.value === interval)?.per ?? "month";
}

/** `archived`: no longer let — off the lists and the counts until restored (rentals/properties#setUnitArchived). */
export type UnitStatus = "vacant" | "occupied" | "notice" | "maintenance" | "archived";
export const UNIT_STATUS_FLOW: ReadonlyArray<{ value: UnitStatus; label: string; tone: string }> = [
  { value: "vacant", label: "Vacant", tone: "#f59e0b" },
  { value: "occupied", label: "Occupied", tone: "#22c55e" },
  { value: "notice", label: "On notice", tone: "#3b82f6" },
  { value: "maintenance", label: "Under repair", tone: "#64748b" },
  { value: "archived", label: "Archived", tone: "#94a3b8" },
];

export type TenancyStatus = "active" | "notice" | "ended" | "evicted";
export const TENANCY_FLOW: ReadonlyArray<{ value: TenancyStatus; label: string; tone: string }> = [
  { value: "active", label: "Active", tone: "#22c55e" },
  { value: "notice", label: "On notice", tone: "#3b82f6" },
  { value: "ended", label: "Ended", tone: "#64748b" },
  { value: "evicted", label: "Evicted", tone: "#ef4444" },
];

export type PeriodStatus = "upcoming" | "due" | "partial" | "paid" | "overdue" | "waived";
export const PERIOD_STATUS_FLOW: ReadonlyArray<{ value: PeriodStatus; label: string; tone: string }> = [
  { value: "upcoming", label: "Upcoming", tone: "#3b82f6" },
  { value: "due", label: "Due", tone: "#f59e0b" },
  { value: "partial", label: "Part paid", tone: "#eab308" },
  { value: "overdue", label: "Overdue", tone: "#ef4444" },
  { value: "paid", label: "Paid", tone: "#22c55e" },
  { value: "waived", label: "Waived", tone: "#64748b" },
];

export function periodStatusLabel(status: string | undefined): string {
  return PERIOD_STATUS_FLOW.find((s) => s.value === status)?.label ?? (status || "—");
}

export function periodStatusTone(status: string | undefined): string {
  return PERIOD_STATUS_FLOW.find((s) => s.value === status)?.tone ?? "#64748b";
}

/** The recurring charges a landlord adds to every bill. */
export const CHARGE_KEYS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "power_fixed", label: "Power (fixed)" },
  { value: "water", label: "Water" },
  { value: "garbage", label: "Garbage" },
  { value: "security", label: "Security" },
  { value: "service", label: "Service charge" },
  { value: "cleaning", label: "Cleaning" },
  { value: "other", label: "Other" },
];

export function chargeLabel(key: string | undefined, fallback?: string): string {
  return CHARGE_KEYS.find((c) => c.value === key)?.label ?? fallback ?? "Charge";
}

// ── What a tenant pays — guarded by Team → Permissions → Set rent ──────────

type ChargeLike = { key?: string; label?: string; amount?: number | string };

/** A charge list as it is stored: labelled, whole shillings, no zero lines. */
export function normalCharges(list: ReadonlyArray<ChargeLike> | undefined | null): Array<{ key: string; label: string; amount: number }> {
  return (list || [])
    .map((c) => ({ key: String(c.key || "other"), label: String(c.label || chargeLabel(c.key)), amount: Math.round(Number(c.amount) || 0) }))
    .filter((c) => c.amount > 0);
}

function chargesKey(list: ReadonlyArray<ChargeLike> | undefined | null): string {
  return normalCharges(list)
    .map((c) => `${c.key}|${c.label.trim().toLowerCase()}|${c.amount}`)
    .sort()
    .join(";");
}

/** Deposit months as a move-in reads them: none set means one month. */
export function depositMonthsOf(months: number | undefined | null): number {
  return months === undefined || months === null ? 1 : Math.max(0, Math.round(Number(months) || 0));
}

/** The deposit a unit asks at move-in — its rent times its deposit months. */
export function unitDeposit(unit: { rent?: number; depositMonths?: number | null } | null | undefined): number {
  return Math.round((Number(unit?.rent) || 0) * depositMonthsOf(unit?.depositMonths));
}

export interface RentTerms {
  rent?: number;
  rentInterval?: string;
  depositMonths?: number;
  charges?: ReadonlyArray<ChargeLike>;
}

export type RentTerm = "rent" | "interval" | "deposit" | "charges";

/**
 * Which of a unit's or a tenancy's money terms an edit changes ([] = none).
 * A term the edit leaves out is left alone, and each is compared the way it
 * is stored — so re-saving a unit to fix its floor needs no permission.
 */
export function rentTermsChanged(current: RentTerms, next: RentTerms): RentTerm[] {
  const out: RentTerm[] = [];
  if (next.rent !== undefined && Math.round(Number(next.rent) || 0) !== Math.round(Number(current.rent) || 0)) out.push("rent");
  if (next.rentInterval !== undefined && (next.rentInterval || "monthly") !== (current.rentInterval || "monthly")) out.push("interval");
  if (next.depositMonths !== undefined && depositMonthsOf(next.depositMonths) !== depositMonthsOf(current.depositMonths)) out.push("deposit");
  if (next.charges !== undefined && chargesKey(next.charges) !== chargesKey(current.charges)) out.push("charges");
  return out;
}

/** "the rent and the charges" — what an edit changed, for a refusal. */
export function rentTermsText(terms: ReadonlyArray<RentTerm>): string {
  const words: Record<RentTerm, string> = { rent: "the rent", interval: "how often it is paid", deposit: "the deposit", charges: "the charges" };
  const list = terms.map((t) => words[t]);
  return list.length <= 1 ? list.join("") : `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
}

export type ListingType = "rent" | "sale";
export const PROPERTY_TYPES: ReadonlyArray<{ value: string; label: string; land?: boolean }> = [
  { value: "house", label: "House" },
  { value: "apartment", label: "Apartment / flat" },
  { value: "room", label: "Room" },
  { value: "shop", label: "Shop" },
  { value: "office", label: "Office" },
  { value: "warehouse", label: "Warehouse" },
  { value: "building", label: "Whole building" },
  { value: "land", label: "Land / plot", land: true },
];

export const TENURES: ReadonlyArray<{ value: string; label: string; hint: string }> = [
  { value: "mailo", label: "Mailo", hint: "Registered under the Mailo system — the commonest title around Kampala" },
  { value: "freehold", label: "Freehold", hint: "Owned outright, forever" },
  { value: "leasehold", label: "Leasehold", hint: "Held on a lease — say how many years remain" },
  { value: "customary", label: "Customary", hint: "Held under customary tenure; a certificate may be in process" },
];

export const TITLE_STATUSES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "ready", label: "Title in hand" },
  { value: "processing", label: "Title being processed" },
  { value: "none", label: "No title" },
];

export const PROPERTY_AMENITIES: ReadonlyArray<string> = [
  "Water", "Power", "Tarmac road", "Security", "Perimeter wall", "Borehole", "Water tank",
  "Generator", "Solar", "Parking", "Lift", "Garden", "Servants' quarters", "Boys' quarters",
  "Fibre internet", "Air conditioning", "Furnished", "Gated estate", "CCTV", "Balcony",
  // What is inside a flat (Hoxton Kesington, Oct 2026).
  "Water heater", "Washing machine", "Cooker", "Wi-Fi", "Reading room", "Visitors' book",
];

/**
 * The amenities offered as ticks. Furnished has its own switch on the unit
 * and on the listing, so it is not offered twice; one kept from before shows
 * as the owner's own and can be taken off.
 */
export const AMENITY_CHOICES: ReadonlyArray<string> = PROPERTY_AMENITIES.filter((a) => a !== "Furnished");

export const PROPERTY_PLACEHOLDER_IMAGE = "/images/placeholder-product.jpg";

/** Platform categories for the "property" product industry (seeded once). */
export const PROPERTY_CATEGORY_SEED: ReadonlyArray<{ name: string; slug: string; icon: string; description: string }> = [
  { name: "Houses", slug: "houses", icon: "🏠", description: "Whole houses for rent or sale" },
  { name: "Apartments", slug: "apartments", icon: "🏢", description: "Flats and apartments" },
  { name: "Rooms", slug: "rooms-to-let", icon: "🚪", description: "Single rooms, doubles, self-contained" },
  { name: "Shops & stalls", slug: "shops-stalls", icon: "🛍️", description: "Retail space in arcades and on the street" },
  { name: "Offices", slug: "offices", icon: "🗄️", description: "Office space and business centres" },
  { name: "Land", slug: "land", icon: "🌱", description: "Plots and acreage" },
  { name: "Commercial buildings", slug: "commercial-buildings", icon: "🏬", description: "Whole buildings and arcades" },
  { name: "Warehouses", slug: "warehouses", icon: "🏭", description: "Stores, godowns and industrial space" },
];

// ── Predicates ─────────────────────────────────────────────────────────────

/** A listing on the website: any product of the property industry. */
export function isPropertyListing(product: any): boolean {
  if (!product) return false;
  return product.industry === PROPERTY_INDUSTRY_SLUG || !!product.property;
}

export function isForRent(product: any): boolean {
  return product?.property?.listingType === "rent";
}

export function isForSaleListing(product: any): boolean {
  return product?.property?.listingType === "sale";
}

/**
 * The public face of the bucket — never the agent's client or their cut. The
 * plot (`plotM2`/`plotUnit`) and the price in another currency
 * (`foreignPrice`) are public: they are what the website shows.
 */
export function publicPropertyDetails(p: any): any {
  if (!p) return p;
  const { ownerName: _o, ownerPhone: _op, commissionPct: _c, ...rest } = p;
  return rest;
}

// ── Rent in dollars (Hoxton Apartments, Oct 2026) ───────────────────────────
//
// "Some apartments accept dollars exclusively… the receipt should show USD
// 1,000, while our main financial reports can show the equivalent in UGX. The
// system should retain the exchange rate used for each transaction." A unit
// (and the tenancy made from it) keeps its rent and charges in ONE currency;
// every bill is raised at the brand's rate that day and keeps it (rentPeriods
// .fx, orders.fx); every payment keeps what came in and the rate it came in
// at (paymentHistory[].fx). The books — total, paid, balance, Cash Flow — stay
// in shillings, so nothing already written changes when the rate moves.

/** The currency a unit or tenancy is let in — UGX unless it says otherwise. */
export function rentCurrencyOf(x: { currency?: string | null } | null | undefined): string {
  const code = String(x?.currency || "").toUpperCase();
  return isForeignCode(code) ? code : BASE_CURRENCY;
}

export function isForeignRent(x: { currency?: string | null } | null | undefined): boolean {
  return rentCurrencyOf(x) !== BASE_CURRENCY;
}

/** A bill's rate, as kept on it: the currency, UGX for one unit, the day. */
export interface RentFx {
  code: string;
  rate: number;
  day?: number;
}

/** A rate the desk typed or the brand keeps, cleaned — or null for shillings. */
export function cleanRentFx(x: { code?: string | null; rate?: number | null; day?: number | null } | null | undefined): RentFx | null {
  if (!x || !isForeignCode(x.code)) return null;
  const rate = cleanRate(x.rate);
  if (rate === null) return null;
  return { code: String(x.code).toUpperCase(), rate, ...(typeof x.day === "number" && x.day > 0 ? { day: Math.round(x.day) } : {}) };
}

/**
 * A bill's money in shillings from terms kept in another currency: the rent,
 * each charge and the total at the rate, plus the total as the tenant reads
 * it ("USD 1,100"). Shilling terms pass through untouched.
 */
export function rentBillMoney(
  terms: { rent: number; charges?: ReadonlyArray<{ key: string; label: string; amount: number }> | null },
  fx: RentFx | null | undefined,
): { rent: number; charges: Array<{ key: string; label: string; amount: number }>; total: number; foreignTotal: number | null } {
  const charges = (terms.charges || []).map((c) => ({ key: c.key, label: c.label, amount: Number(c.amount) || 0 }));
  if (!fx || !(fx.rate > 0)) {
    const rent = Math.round(Number(terms.rent) || 0);
    const ugxCharges = charges.map((c) => ({ ...c, amount: Math.round(c.amount) }));
    return { rent, charges: ugxCharges, total: rent + sumCharges(ugxCharges), foreignTotal: null };
  }
  const rent = toBase(Number(terms.rent) || 0, fx.rate);
  const ugxCharges = charges.map((c) => ({ ...c, amount: toBase(c.amount, fx.rate) }));
  const foreignTotal = roundIn((Number(terms.rent) || 0) + charges.reduce((s, c) => s + c.amount, 0), fx.code);
  return { rent, charges: ugxCharges, total: rent + sumCharges(ugxCharges), foreignTotal };
}

/** What came in, in the tenant's currency, as shillings at the rate it came in at. */
export function paymentInBase(fx: { code: string; amount: number; rate: number } | null | undefined, ugx: number): number {
  if (fx && isForeignCode(fx.code) && fx.rate > 0 && fx.amount > 0) return toBase(fx.amount, fx.rate);
  return Math.round(Number(ugx) || 0);
}

/**
 * Shillings told in the bill's currency: "USD 500" — with "UGX 1,850,000"
 * beside it when asked. Shilling bills read as shillings.
 */
export function rentMoney(ugx: number, fx: { code?: string | null; rate?: number | null } | null | undefined, opts: { both?: boolean } = {}): string {
  const n = Math.round(Number(ugx) || 0);
  const base = `UGX ${n.toLocaleString("en-US")}`;
  if (!fx || !isForeignCode(fx.code) || !(Number(fx.rate) > 0)) return base;
  const foreign = formatIn(fromBase(n, Number(fx.rate), String(fx.code)), String(fx.code));
  return opts.both ? `${foreign} · ${base}` : foreign;
}

/** "USD 500 / month" — a unit's or tenancy's own terms, in their currency. */
export function rentTermsMoney(amount: number, currency: string | null | undefined): string {
  return formatIn(Number(amount) || 0, rentCurrencyOf({ currency }));
}

// ── Land in decimals, prices in dollars (Nakasero Estates, Oct 2026) ───────

/** A land size as it is kept: m² (two places) with the unit it was typed in — or null. */
export function cleanLandSize(m2: unknown, unit: unknown): { m2: number; unit: LandUnit } | null {
  const v = Math.round((Number(m2) || 0) * 100) / 100;
  if (!(v > 0) || v > 1e9) return null;
  // A unit that is not a land unit is read as m² — the size itself is in m² either way.
  return { m2: v, unit: isLandUnit(unit) ? unit : "m2" };
}

/**
 * A listing's bucket as it is stored. The plot is a measure (m² and the
 * unit it was typed in) or nothing; the price in another currency is kept
 * only when it is a real currency and amount. A plot of land also keeps its
 * size in `sizeSqm`, which the older pages and cards read.
 */
export function cleanPropertyDetails<T extends Record<string, unknown>>(p: T): T {
  const out: Record<string, unknown> = { ...p };
  const plot = cleanLandSize(p.plotM2, p.plotUnit);
  if (plot) {
    out.plotM2 = plot.m2;
    out.plotUnit = plot.unit;
    if (p.propertyType === "land") out.sizeSqm = Math.round(plot.m2);
  } else {
    delete out.plotM2;
    delete out.plotUnit;
  }
  const foreign = cleanForeignPrice(p.foreignPrice as { code?: string; amount?: number } | null | undefined);
  if (foreign) out.foreignPrice = foreign;
  else delete out.foreignPrice;
  // What it has and its house rules, kept the way a unit keeps them.
  const amenities = cleanAmenities(p.amenities as unknown[] | undefined);
  if (amenities.length) out.amenities = amenities;
  else delete out.amenities;
  const kitchen = cleanKitchen(p.kitchen);
  if (kitchen) out.kitchen = kitchen;
  else delete out.kitchen;
  const rules = cleanHouseRules(p.rules as HouseRules | undefined);
  if (rules) out.rules = rules;
  else delete out.rules;
  return out as T;
}

/**
 * The plot as a listing reads it: "30 decimals (1,214 m²)" when it was
 * measured, else the words the agent typed ("50x100 ft"), else — for a plot
 * of land listed before decimals — its m². Empty when there is none.
 */
export function plotLabel(
  d: { plotM2?: number; plotUnit?: string; plotSize?: string; sizeSqm?: number; propertyType?: string } | null | undefined,
): string {
  if (!d) return "";
  const measured = formatLand(d.plotM2, d.plotUnit);
  if (measured) return measured;
  const typed = String(d.plotSize ?? "").trim();
  if (typed) return typed;
  return d.propertyType === "land" ? formatLand(d.sizeSqm, "m2") : "";
}

/** A price in another currency as the website shows it. `approx` = worked out at the rate, not set by the owner. */
export interface ForeignShown {
  code: string;
  amount: number;
  first: boolean;
  approx?: true;
}

/**
 * The price shown beside the UGX one on a website: the one the owner set in
 * that currency, exactly — else the UGX price at the website currency's rate,
 * to the whole dollar and marked approximate ("2 billion feels a lot; 500,000
 * dollars sounds small"). Null when there is neither.
 */
export function websiteForeignPrice(
  ugx: number,
  fixed: { code: string; amount: number } | null | undefined,
  site: (Fx & { first: boolean }) | null | undefined,
): ForeignShown | null {
  const shown = websitePrice(Number(ugx) || 0, fixed ?? null, site ?? null);
  if (!shown) return null;
  const own = !!fixed && fixed.amount > 0 && isForeignCode(fixed.code);
  return own ? shown : { ...shown, amount: Math.round(shown.amount), approx: true };
}

/** "USD 495,000", or "≈ USD 540,541" for a price worked out at the rate. */
export function foreignPriceText(p: { code: string; amount: number; approx?: boolean } | null | undefined): string {
  if (!p) return "";
  return `${p.approx ? "≈ " : ""}${formatIn(p.amount, p.code)}`;
}

// ── What a unit has, and its house rules (Hoxton Kesington, Oct 2026) ──────
//
// "When registering a unit, specify what it has — balcony, bathrooms, closed
// or open kitchen, reading room, security, visitors' book, water heater… —
// so tenants and guests know before booking", and "per unit, whether
// children are allowed, whether couples are allowed, the maximum number of
// occupants, any other rules". The UNIT keeps them: the register is where a
// landlord describes a unit. A listing tied to the unit shows the unit's —
// rentals/listings writes them onto the listing when it is tied and whenever
// the unit is saved, so they are typed once. A listing for a property the
// brand does not manage keeps its own, typed in the Property Studio.
//
// Amenities are ONE list: the ticks (AMENITY_CHOICES) and the owner's own
// lines ("Rooftop terrace"). Children and couples are allowed, not allowed,
// or not said (absent); the website shows only what was said.

export type KitchenKind = "closed" | "open";
export const KITCHEN_KINDS: ReadonlyArray<{ value: KitchenKind; label: string }> = [
  { value: "closed", label: "Closed kitchen" },
  { value: "open", label: "Open kitchen" },
];

export interface HouseRules {
  /** Children allowed? Absent = not said. */
  children?: boolean;
  /** Couples allowed? Absent = not said. */
  couples?: boolean;
  /** The most people who may live or stay in it. */
  maxOccupants?: number;
  /** The owner's own rules, one line each: "No pets", "Quiet after 10 pm". */
  custom?: string[];
}

export interface UnitFeatures {
  /** The ticks and the owner's own, in one list. */
  amenities?: string[];
  bathrooms?: number;
  kitchen?: KitchenKind;
  rules?: HouseRules;
}

export const MAX_AMENITIES = 40;
export const AMENITY_MAX_LEN = 60;
export const MAX_HOUSE_RULES = 20;
export const HOUSE_RULE_MAX_LEN = 140;
/** Bathrooms and people are whole numbers, 1 to this. */
export const MAX_FEATURE_COUNT = 99;

/** One line as typed: spaces collapsed, trimmed, cut to `max`. */
function cleanLine(raw: unknown, max: number): string {
  return String(raw ?? "").replace(/\s+/g, " ").trim().slice(0, max).trim();
}

/** "wi-fi" → "Wi-Fi": an amenity that is on the list takes the list's spelling. Null when it is the owner's own. */
export function presetAmenity(name: unknown): string | null {
  const key = cleanLine(name, AMENITY_MAX_LEN).toLowerCase();
  if (!key) return null;
  return PROPERTY_AMENITIES.find((a) => a.toLowerCase() === key) ?? null;
}

/** An amenity list as it is kept: trimmed, each once (any case), the list's spelling, at most MAX_AMENITIES. */
export function cleanAmenities(list: ReadonlyArray<unknown> | null | undefined): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of Array.isArray(list) ? list : []) {
    const line = cleanLine(raw, AMENITY_MAX_LEN);
    if (!line) continue;
    const name = presetAmenity(line) ?? line;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
    if (out.length >= MAX_AMENITIES) break;
  }
  return out;
}

/** The ticks and the owner's own lines, apart — the editors show them differently. */
export function splitAmenities(list: ReadonlyArray<unknown> | null | undefined): { picked: string[]; own: string[] } {
  const clean = cleanAmenities(list);
  const ticks = new Set(AMENITY_CHOICES.map((a) => a.toLowerCase()));
  return {
    picked: clean.filter((a) => ticks.has(a.toLowerCase())),
    own: clean.filter((a) => !ticks.has(a.toLowerCase())),
  };
}

export function cleanKitchen(v: unknown): KitchenKind | undefined {
  return v === "closed" || v === "open" ? v : undefined;
}

export function kitchenLabel(v: unknown): string {
  return KITCHEN_KINDS.find((k) => k.value === v)?.label ?? "";
}

/** A whole number from 1 to MAX_FEATURE_COUNT, or nothing (0 and blanks mean not said). */
export function cleanCount(v: unknown): number | undefined {
  const n = Math.round(Number(v));
  return Number.isFinite(n) && n >= 1 && n <= MAX_FEATURE_COUNT ? n : undefined;
}

/** House rules as they are kept — or undefined when nothing is said. */
export function cleanHouseRules(r: HouseRules | null | undefined): HouseRules | undefined {
  if (!r || typeof r !== "object") return undefined;
  const out: HouseRules = {};
  if (typeof r.children === "boolean") out.children = r.children;
  if (typeof r.couples === "boolean") out.couples = r.couples;
  const max = cleanCount(r.maxOccupants);
  if (max) out.maxOccupants = max;
  const custom: string[] = [];
  const seen = new Set<string>();
  for (const raw of Array.isArray(r.custom) ? r.custom : []) {
    const line = cleanLine(raw, HOUSE_RULE_MAX_LEN);
    if (!line || seen.has(line.toLowerCase())) continue;
    seen.add(line.toLowerCase());
    custom.push(line);
    if (custom.length >= MAX_HOUSE_RULES) break;
  }
  if (custom.length) out.custom = custom;
  return Object.keys(out).length ? out : undefined;
}

/** What a unit (or a listing) has and its rules, cleaned: a key only when something is said. */
export function cleanUnitFeatures(f: { amenities?: unknown; bathrooms?: unknown; kitchen?: unknown; rules?: unknown } | null | undefined): UnitFeatures {
  const out: UnitFeatures = {};
  if (!f) return out;
  const amenities = cleanAmenities(f.amenities as unknown[] | undefined);
  if (amenities.length) out.amenities = amenities;
  const bathrooms = cleanCount(f.bathrooms);
  if (bathrooms) out.bathrooms = bathrooms;
  const kitchen = cleanKitchen(f.kitchen);
  if (kitchen) out.kitchen = kitchen;
  const rules = cleanHouseRules(f.rules as HouseRules | undefined);
  if (rules) out.rules = rules;
  return out;
}

export function hasUnitFeatures(f: Parameters<typeof cleanUnitFeatures>[0]): boolean {
  return Object.keys(cleanUnitFeatures(f)).length > 0;
}

/** Compares two descriptions as they are kept — a save that changes nothing writes nothing. */
export function unitFeaturesKey(f: Parameters<typeof cleanUnitFeatures>[0]): string {
  const c = cleanUnitFeatures(f);
  return JSON.stringify([c.amenities ?? [], c.bathrooms ?? 0, c.kitchen ?? "", c.rules?.children ?? null, c.rules?.couples ?? null, c.rules?.maxOccupants ?? 0, c.rules?.custom ?? []]);
}

/**
 * A listing's bucket with its unit's amenities, kitchen and house rules.
 * The unit's, once it has any: the register is where they are kept. A unit
 * that has none leaves the listing's own alone — unless `clearWhenNone`
 * (the unit's were just taken off), when the listing loses them too. The
 * bathrooms are also the listing's own headline figure, so the unit's count
 * replaces it only when the unit has one.
 */
export function withUnitFeatures<T extends Record<string, unknown>>(
  property: T,
  unit: Parameters<typeof cleanUnitFeatures>[0],
  opts: { clearWhenNone?: boolean } = {},
): T {
  const f = cleanUnitFeatures(unit);
  if (!Object.keys(f).length && !opts.clearWhenNone) return property;
  const out: Record<string, unknown> = { ...property };
  for (const key of ["amenities", "kitchen", "rules"] as const) {
    if (f[key] !== undefined) out[key] = f[key];
    else delete out[key];
  }
  if (f.bathrooms !== undefined) out.bathrooms = f.bathrooms;
  return out as T;
}

/** "Up to 4 people", "One person only". */
export function occupantsText(n: number): string {
  return n === 1 ? "One person only" : `Up to ${n} people`;
}

export interface HouseRuleLine {
  key: string;
  text: string;
  /** allowed → a tick, not allowed → a cross, else a plain line. */
  tone: "yes" | "no" | "note";
}

/** The house rules as the website lists them, in a set order, the owner's own last. */
export function houseRuleLines(r: HouseRules | null | undefined): HouseRuleLine[] {
  const rules = cleanHouseRules(r);
  if (!rules) return [];
  const out: HouseRuleLine[] = [];
  if (rules.maxOccupants) out.push({ key: "max", text: occupantsText(rules.maxOccupants), tone: "note" });
  if (rules.children !== undefined) out.push({ key: "children", text: rules.children ? "Children allowed" : "No children", tone: rules.children ? "yes" : "no" });
  if (rules.couples !== undefined) out.push({ key: "couples", text: rules.couples ? "Couples allowed" : "No couples", tone: rules.couples ? "yes" : "no" });
  (rules.custom ?? []).forEach((text, i) => out.push({ key: `own-${i}`, text, tone: "note" }));
  return out;
}

/** The short rules, for a card: "Up to 4 people · No children · Couples allowed". The owner's own lines are on the listing page. */
export function houseRuleChips(r: HouseRules | null | undefined): string[] {
  return houseRuleLines(r).filter((l) => !l.key.startsWith("own-")).map((l) => l.text);
}

/**
 * One line for a unit's row on the register: "2 bathrooms · Open kitchen ·
 * Balcony, Wi-Fi, Cooker +2 · Up to 4 people · No children · 1 more rule".
 * Empty when nothing is said.
 */
export function unitFeaturesLine(f: Parameters<typeof cleanUnitFeatures>[0], shownAmenities = 3): string {
  const c = cleanUnitFeatures(f);
  const parts: string[] = [];
  if (c.bathrooms) parts.push(`${c.bathrooms} bathroom${c.bathrooms === 1 ? "" : "s"}`);
  if (c.kitchen) parts.push(kitchenLabel(c.kitchen));
  const a = c.amenities ?? [];
  if (a.length) parts.push(a.length > shownAmenities ? `${a.slice(0, shownAmenities).join(", ")} +${a.length - shownAmenities}` : a.join(", "));
  parts.push(...houseRuleChips(c.rules));
  const own = c.rules?.custom?.length ?? 0;
  if (own) parts.push(`${own} more rule${own === 1 ? "" : "s"}`);
  return parts.join(" · ");
}

/** Yes / no / not said, as the editors hold it. */
export type RuleChoice = "" | "yes" | "no";

/** The editors' copy of a description: text boxes hold text, choices hold "" for not said. */
export interface UnitFeaturesForm {
  /** Ticked from AMENITY_CHOICES. */
  picked: string[];
  /** Typed by the owner. */
  own: string[];
  bathrooms: string;
  kitchen: "" | KitchenKind;
  children: RuleChoice;
  couples: RuleChoice;
  maxOccupants: string;
  /** The owner's own rules. */
  rules: string[];
}

export const EMPTY_FEATURES_FORM: UnitFeaturesForm = {
  picked: [],
  own: [],
  bathrooms: "",
  kitchen: "",
  children: "",
  couples: "",
  maxOccupants: "",
  rules: [],
};

const choiceOf = (b: unknown): RuleChoice => (b === true ? "yes" : b === false ? "no" : "");
const boolOf = (c: RuleChoice): boolean | undefined => (c === "yes" ? true : c === "no" ? false : undefined);

export function unitFeaturesForm(f: Parameters<typeof cleanUnitFeatures>[0]): UnitFeaturesForm {
  const c = cleanUnitFeatures(f);
  const { picked, own } = splitAmenities(c.amenities);
  return {
    picked,
    own,
    bathrooms: c.bathrooms ? String(c.bathrooms) : "",
    kitchen: c.kitchen ?? "",
    children: choiceOf(c.rules?.children),
    couples: choiceOf(c.rules?.couples),
    maxOccupants: c.rules?.maxOccupants ? String(c.rules.maxOccupants) : "",
    rules: c.rules?.custom ?? [],
  };
}

/** The editors' copy back to what is kept — the ticks first, then the owner's own. */
export function unitFeaturesFromForm(form: UnitFeaturesForm): UnitFeatures {
  return cleanUnitFeatures({
    amenities: [...form.picked, ...form.own],
    bathrooms: form.bathrooms,
    kitchen: form.kitchen,
    rules: { children: boolOf(form.children), couples: boolOf(form.couples), maxOccupants: Number(form.maxOccupants) || undefined, custom: form.rules },
  });
}

export function propertyTypeLabel(value: string | undefined): string {
  return PROPERTY_TYPES.find((t) => t.value === value)?.label ?? (value || "Property");
}

export function tenureLabel(value: string | undefined): string {
  return TENURES.find((t) => t.value === value)?.label ?? (value || "");
}

export function unitKindLabel(value: string | undefined): string {
  return UNIT_KINDS.find((k) => k.value === value)?.label ?? (value || "Unit");
}

export function pickLabel(list: ReadonlyArray<{ value: string; label: string }>, value: string | undefined): string {
  return list.find((o) => o.value === value)?.label ?? (value || "—");
}

// ── Period maths (dayInt) ──────────────────────────────────────────────────

export function daysInMonth(year: number, month1: number): number {
  return new Date(Date.UTC(year, month1, 0)).getUTCDate();
}

/** The same day-of-month `months` later, clamped to the month's length. */
export function addMonthsDay(day: number, months: number): number {
  const y = Math.floor(day / 10000);
  const m = Math.floor((day % 10000) / 100);
  const d = day % 100;
  const idx = (m - 1) + months;
  const ny = y + Math.floor(idx / 12);
  const nm = ((idx % 12) + 12) % 12 + 1;
  const nd = Math.min(d, daysInMonth(ny, nm));
  return ny * 10000 + nm * 100 + nd;
}

/** "2026-10" for any day in October 2026. */
export function monthKeyOf(day: number): string {
  const y = Math.floor(day / 10000);
  const m = Math.floor((day % 10000) / 100);
  return `${y}-${String(m).padStart(2, "0")}`;
}

/** First and last dayInt of a "YYYY-MM" month. */
export function monthRange(monthKey: string): [number, number] {
  const [ys, ms] = monthKey.split("-");
  const y = Number(ys);
  const m = Number(ms);
  return [y * 10000 + m * 100 + 1, y * 10000 + m * 100 + daysInMonth(y, m)];
}

export function shiftMonthKey(monthKey: string, months: number): string {
  const [first] = monthRange(monthKey);
  return monthKeyOf(addMonthsDay(first, months));
}

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function monthKeyLabel(monthKey: string): string {
  const [ys, ms] = monthKey.split("-");
  return `${MONTHS_SHORT[Number(ms) - 1] ?? ms} ${ys}`;
}

/**
 * Rent is paid IN ADVANCE, so a period starts on its due date: period k of a
 * tenancy runs [start + k·months, start + (k+1)·months). The tenant who
 * moved in on the 14th pays on the 14th — that is what a landlord means by
 * "due day".
 */
export function periodStartFor(startDay: number, interval: string | undefined, index: number): number {
  return addMonthsDay(startDay, index * intervalMonths(interval));
}

// ── Terms changed mid-lease (Hoxton Kesington, Nov 2026) ─────────────────
//
// A tenant who paid a quarter up front may go monthly after it; a fixed lease
// may turn open, or end on another day. The schedule then runs on the new
// terms from a chosen bill: bills before it stay exactly as they were, bills
// from it on are raised at the new interval and rent, counted from that day
// (rentals/tenancies#changeTerms).

export type TermsAnchor = {
  startDay: number;
  rentInterval?: string;
  termsFromDay?: number | null;
  termsFromIndex?: number | null;
};

/** Where bill `index` of a tenancy starts — on the terms in force for that bill. */
export function tenancyPeriodStart(t: TermsAnchor, index: number): number {
  const from = t.termsFromIndex ?? 0;
  if (t.termsFromDay && index >= from) return periodStartFor(t.termsFromDay, t.rentInterval, index - from);
  return periodStartFor(t.startDay, t.rentInterval, index);
}

export type TermsBill = {
  periodStartDay: number;
  periodLabel: string;
  total: number;
  paid: number;
  status: string;
  replacedAt?: number | null;
};
export type TermsChangePoint = { fromDay: number; replaces: TermsBill[] };

/**
 * The days new terms may start on, latest first: the next bill not raised
 * yet, or an earlier bill when it and every bill after it have nothing paid
 * on them — those are written off and raised again on the new terms. A bill
 * with money on it is never replaced. `replaces` lists the bills written off
 * (a bill already waived is replaced too, but owes nothing).
 */
export function termsChangePoints(bills: ReadonlyArray<TermsBill>, nextStart: number): TermsChangePoint[] {
  const live = bills.filter((b) => !b.replacedAt).sort((a, b) => a.periodStartDay - b.periodStartDay);
  const points: TermsChangePoint[] = [{ fromDay: nextStart, replaces: [] }];
  for (let i = live.length - 1; i >= 0; i--) {
    if ((Number(live[i].paid) || 0) > 0) break;
    points.push({ fromDay: live[i].periodStartDay, replaces: live.slice(i).filter((b) => b.status !== "waived") });
  }
  return points;
}

/** Why new terms cannot be saved, or null. Days are YYYYMMDD. */
export function termsChangeProblem(p: { rent: number; interval: string; fromDay: number; endDay: number | null }): string | null {
  if (!(p.rent > 0)) return "Enter the rent";
  if (!RENT_INTERVALS.some((i) => i.value === p.interval)) return "Pick how often the rent is paid";
  if (p.endDay !== null && p.endDay < p.fromDay) {
    return `The lease would end on ${formatDayInt(p.endDay, true)}, before the new terms start on ${formatDayInt(p.fromDay, true)}. To end the lease, use Give notice or Move out.`;
  }
  return null;
}

export function periodEndFor(periodStartDay: number, interval: string | undefined): number {
  return addDays(addMonthsDay(periodStartDay, intervalMonths(interval)), -1);
}

/** "2026-10" — the month the period starts in. Unique per tenancy. */
export function periodKeyFor(periodStartDay: number): string {
  return monthKeyOf(periodStartDay);
}

/** "Oct 2026" for a month; "Oct – Dec 2026" for a quarter. */
export function periodLabel(periodStartDay: number, interval: string | undefined): string {
  const months = intervalMonths(interval);
  const startKey = monthKeyOf(periodStartDay);
  if (months <= 1) return monthKeyLabel(startKey);
  const endKey = monthKeyOf(addMonthsDay(periodStartDay, months - 1));
  const [sy, sm] = startKey.split("-");
  const [ey, em] = endKey.split("-");
  if (sy === ey) return `${MONTHS_SHORT[Number(sm) - 1]} – ${MONTHS_SHORT[Number(em) - 1]} ${sy}`;
  return `${monthKeyLabel(startKey)} – ${monthKeyLabel(endKey)}`;
}

export function dayOfMonth(day: number): number {
  return day % 100;
}

/** "14th", "1st", "22nd". */
export function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

export function formatDue(day: number): string {
  return isValidDayInt(day) ? formatDayInt(day, true) : "—";
}

// ── What a move-in can take (Hoxton Kesington, Nov 2026) ───────────────────
//
// Rent taken at move-in pays the first bill, then the bills that follow — as
// far as the term goes. A FIXED term is billed up to its last day and no
// further, so money for months after it has nowhere to go: the move-in is
// refused and says so, never kept quietly. (9,000,000 was typed for three
// months on a term set to end after the first; the system took the first
// month and dropped the other two without a word.)

/** How many bills ahead one payment may raise and pay (rentals/periods). */
export const MAX_BILLS_AHEAD = 24;

/**
 * How many bills a fixed term holds: bill k starts on start + k·interval and
 * is raised only while it starts on or before the term's last day. Null for
 * an open tenancy (no last day). Capped, so a mistyped year cannot spin.
 */
export function billsInTerm(startDay: number, endDay: number | null | undefined, interval: string | undefined, cap = 240): number | null {
  if (!endDay) return null;
  let n = 0;
  while (n < cap && periodStartFor(startDay, interval, n) <= endDay) n++;
  return n;
}

/**
 * The refusal when rent taken at move-in is more than the tenancy can bill:
 * the term's last day comes first (`endDay`), or the money runs further
 * ahead than one payment may go. `taken` and `fits` are written money
 * ("UGX 9,000,000"); `fits` is what can be paid.
 */
export function rentPastTermText(p: { taken: string; fits: string; endDay?: number | null }): string {
  if (p.endDay) {
    return `${p.taken} is more than this tenancy can be billed: its term ends on ${formatDayInt(p.endDay, true)}, so ${p.fits} is all it can take. Change the end date (or pick Open), or take ${p.fits}.`;
  }
  return `${p.taken} pays more than ${MAX_BILLS_AHEAD} bills ahead. Take ${p.fits} now and the rest when the later bills are raised.`;
}

/** Default grace before a due bill is called overdue. */
export const DEFAULT_GRACE_DAYS = 5;

/**
 * Where a bill stands. `waived` sticks; otherwise the money and the calendar
 * decide: paid in full → paid; past due + grace with a balance → overdue;
 * due day reached → due (or part paid); still ahead → upcoming (or part paid).
 */
export function periodStatusFor(p: {
  total: number;
  paid: number;
  dueDay: number;
  today: number;
  graceDays?: number;
  waived?: boolean;
}): PeriodStatus {
  if (p.waived) return "waived";
  if (p.paid >= p.total && p.total > 0) return "paid";
  const grace = p.graceDays ?? DEFAULT_GRACE_DAYS;
  if (addDays(p.dueDay, grace) < p.today && p.paid < p.total) return "overdue";
  if (p.dueDay <= p.today) return p.paid > 0 ? "partial" : "due";
  return p.paid > 0 ? "partial" : "upcoming";
}

/** A tenancy's snapshot of its current bill — denormalised onto the row. */
export interface CurrentPeriod {
  periodId: string;
  periodKey: string;
  label: string;
  dueDay: number;
  total: number;
  paid: number;
  balance: number;
  status: PeriodStatus;
}

export function sumCharges(charges: ReadonlyArray<{ amount: number }> | undefined | null): number {
  return (charges || []).reduce((s, c) => s + (Number(c.amount) || 0), 0);
}

/** "Dembe Arcade · Shop 14" */
export function unitLabelOf(propertyName: string | undefined, unitName: string | undefined): string {
  return [propertyName, unitName].filter(Boolean).join(" · ");
}

/** "4-bedroom house, Kira" — how a listing names itself. */
export function listingTitle(p: any): string {
  const d = p?.property || {};
  const type = propertyTypeLabel(d.propertyType);
  const beds = d.bedrooms ? `${d.bedrooms}-bedroom ` : "";
  const where = d.location?.area ? `, ${d.location.area}` : "";
  if (p?.name) return p.name;
  return `${beds}${type.toLowerCase()}${where}`;
}
